import { formatCents, parseMoneyToCents, tt } from '@agos/shared'

import { tableKeyForOrder } from '../table-map/table-status'

import type { OrderCartLine } from './cart'

/**
 * Re-opening an existing order into the builder — `/new-order?edit={id}`.
 *
 * The last unwired piece of the page. `OrderCartLine` already carries `locked`, and `changeQuantity` and
 * `removeLine` already refuse a locked line; nothing ever set the flag, because nothing loaded an order.
 *
 * **The backend has two different writes and they are not interchangeable.** `PATCH /orders/{orderId}`
 * takes both:
 *
 * - `items` — **replaces** every line. `replaceItems` deletes and re-inserts.
 * - `newItems` — **a round.** `addRound`: *"If the kitchen is still working, the new food joins the round
 *   already on the pass — the cook sees one ticket, not two. If the kitchen had finished, it starts a new
 *   round and the order goes back to it."*
 *
 * So sending everything through `items` on a second helping would silently delete the ticket the kitchen
 * is cooking from and re-insert it, which is the wrong instruction to give a busy kitchen. This module
 * splits the cart the way the endpoint expects: lines that were already on the order and are unchanged
 * are left alone, and genuinely new food goes in `newItems`.
 *
 * **What locked means.** Every line carries a `kitchenRound`. Lines below the order's highest round have
 * already been cooked and served from, so they are shown and not edited — the way to remove one is a
 * refund or a storno, not a quiet re-insert. Lines in the highest round are still editable, because that
 * is the round nobody has finished.
 */

export interface LoadedOrder {
  id: number
  orderNumber: string
  status: string
  orderType: string
  customerName: string
  customerPhone: string
  /**
   * Read so it can be diffed - §9.383, FB-681.
   *
   * It was absent from this shape and from the form, so an edit that sent `customerEmail` would have
   * sent an empty one and **cleared the customer's address on every save**. A field is not diffable
   * until it is loaded.
   */
  customerEmail: string
  customerAddress: string
  tableName: string
  notes: string
  paymentMethod: string
  /**
   * The online payment's state - §9.374, and it decides one refusal.
   *
   * A method change is refused only when this reads `online_paid`: the money arrived through that
   * method and relabelling it would make the takings lie. A CardPay order that did **not** go
   * through has nothing against it, which is the owner's case.
   */
  paymentStatus: string
  /** Whether the order is a časenka, and for when — legacy reopens the builder on both. */
  isPreorder: boolean
  scheduledAt: string
  lines: OrderCartLine[]
  maxRound: number
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 1
}

/** `GET /orders/{id}` into the shape the builder holds, with the earlier rounds locked. */
export function loadedOrderOf(payload: unknown): LoadedOrder | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null

  const items = Array.isArray(row.items) ? row.items : []
  const rounds = items.map((entry) => {
    const round = (entry as Record<string, unknown>).kitchenRound
    return typeof round === 'number' ? round : 1
  })
  const maxRound = rounds.length === 0 ? 1 : Math.max(...rounds)

  return {
    id: row.id,
    orderNumber: text(row.orderNumber),
    status: text(row.status),
    orderType: text(row.type),
    customerName: text(row.customerName),
    customerPhone: text(row.customerPhone),
    customerEmail: text(row.customerEmail),
    customerAddress: text(row.customerAddress),
    // §9.202 — `row.tableName` is a field the server does not send: `orders` has no `table_name`
    // column and the orders API no such property. A table order is identified by its
    // `customerName` — *Stôl 5* — which is what `tableKeyForOrder` reads for the board, the map and
    // the dispatch list, and what `buildOrderPayload` says it writes.
    //
    // Reading the absent field wiped the number the *Otvorené stoly* link had already put in the
    // box, and `submitProblems` then refused the save with *Zadajte číslo stola*.
    tableName: tableKeyForOrder(row) ?? '',
    notes: text(row.notes),
    paymentMethod: text(row.paymentMethod) === '' ? 'cash' : text(row.paymentMethod),
    paymentStatus: text(row.paymentStatus),
    isPreorder: row.isPreorder === true,
    scheduledAt: text(row.scheduledAt),
    maxRound,
    lines: items.map((entry) => {
      const item = entry as Record<string, unknown>
      const round = typeof item.kitchenRound === 'number' ? item.kitchenRound : 1
      return {
        productId: typeof item.productId === 'number' ? item.productId : 0,
        productName: text(item.productName),
        unitPrice: item.unitPrice as number,
        quantity: count(item.quantity),
        ...(text(item.categoryName) === '' ? {} : { categoryName: text(item.categoryName) }),
        ...(text(item.boxGroupId) === '' ? {} : { boxGroupId: text(item.boxGroupId) }),
        ...(text(item.boxGroupName) === '' ? {} : { boxGroupName: text(item.boxGroupName) }),
        ...(text(item.boxSlotLabel) === '' ? {} : { boxSlotLabel: text(item.boxSlotLabel) }),
        // Everything below the highest round has been cooked from.
        ...(round < maxRound ? { locked: true } : {}),
        // Carried so the split below can tell an existing line from a new one.
        existingId: typeof item.id === 'number' ? item.id : undefined,
      }
    }),
  }
}

/**
 * Said above the earlier rounds, so what changing one *does* is on the screen - §9.374, FB-668.
 *
 * **It used to say they could not be changed**, and the owner asked for exactly that: *"Add
 * posibility to edit previous round… they ordered 3x čapovaná kofola, but when you start čapovať
 * them, they will tell you: «We want only two, not three»."* So the note stops refusing and starts
 * explaining: a change to a line that is already on the order **rewrites the order** rather than
 * adding a round, so the kitchen sees nothing new for it. That is legacy's behaviour and the one
 * thing an operator cannot guess from the screen.
 */
export function lockedNote(order: LoadedOrder): string {
  const locked = order.lines.filter((line) => line.locked === true).length
  if (locked === 0) return ''
  return `${tt('Skoršie kolá')} (${locked}) ${tt('sa dajú opraviť - zmena ich prepíše bez nového kola pre kuchyňu. Doobjednávka je vždy nové kolo.')}`
}

/** One cart line as the endpoint wants it, for either `newItems` or `items`. */
function itemOf(line: OrderCartLine & { existingId?: number }): Record<string, unknown> {
  return {
      productId: line.productId,
      productName: line.productName,
      quantity: line.quantity,
      unitPrice: parseMoneyToCents(line.unitPrice) / 100,
      ...(line.boxGroupId === undefined ? {} : { boxGroupId: line.boxGroupId }),
      ...(line.boxGroupName === undefined ? {} : { boxGroupName: line.boxGroupName }),
      ...(line.boxSlotLabel === undefined ? {} : { boxSlotLabel: line.boxSlotLabel }),
      // §10A.14 and §10A.26. Without these a composed line arriving as a round has nothing to be
      // bounded against: `boundedByParts` with no parts returns the price it was sent, so a kombo
      // added to a table that has already ordered was priced entirely by the client. Measured at
      // 99,00 € on the running stack before this line existed.
      ...(line.parts && line.parts.length > 0
        ? {
            parts: line.parts.map((part) => ({
              ...(part.productId === undefined ? {} : { productId: part.productId }),
              name: part.name,
              unitPrice: part.unitPriceCents / 100,
              quantity: part.quantity,
            })),
          }
        : {}),
      ...(line.composition ? { composition: line.composition } : {}),
      // §10A.34 — an edit re-prices, and a re-price that does not know the size charges the base.
      // Measured before this was sent: a 50 cm pizza came back from an edit at the 32 cm price,
      // under a name that had lost the size.
      ...(line.size === undefined ? {} : { size: line.size }),
  }
}

/**
 * What an edit sends - §9.374, FB-668, and it used to be able to send only one of these three.
 *
 * **The old version returned `{newItems}` or `null`**, where `newItems` was *lines with no
 * `existingId`*. Four things an operator can do on that screen therefore reached the server as
 * nothing at all, and the owner reported all four in one batch:
 *
 * - **adding a product the order already has.** `addProduct` merged into the existing line, so the
 *   quantity rose on a line carrying an `existingId` and the payload came out empty: *"Zatiaľ nič
 *   nepridané"*, and a guest drank a kofola nobody was charged for. (`addProduct` no longer merges
 *   into a loaded line, so this is now an added line as well - but the rest of this function is what
 *   makes the other three possible.)
 * - **correcting three kofolas to two**, the owner's own example, while they are being poured.
 * - **removing a line.**
 * - **switching a failed CardPay order to *Karta kuriér***, which could not be sent at all.
 *
 * **Legacy has three branches and its comments are the specification** (`restaurant.tsx:847`):
 * replace the whole array when a line that was already there changed - *"covers removing a beef bowl
 * that came in damaged, decrementing a previous-round drink"* - send only the new lines when nothing
 * else moved, so the kitchen gets a round, and send the scalars alone when neither happened.
 *
 * **`items` rewrites the order and starts no round**, which is the trade legacy names out loud and
 * the reason the three branches are ordered this way: a correction is not a round, and food that is
 * genuinely new is.
 */
export interface EditScalars {
  notes?: string
  paymentMethod?: string
  /** Cleared alongside a method change on an order whose online payment never landed. */
  paymentStatus?: string | null
  discount?: number
  manualDiscountType?: string
  manualDiscountValue?: number
  manualDiscountOperator?: string | null
  /**
   * Everything else the form can change and the save could not send - §9.383, FB-681.
   *
   * The owner asked for one of them (*"changing objednávka from reštaurácia to osobný odber - its
   * not possible"*) and every field here had the same defect: the control worked, the save dropped
   * it, and the screen said *Objednávka uložená*. `OrderEditController.EDITABLE` has taken all of
   * them since it was written.
   */
  type?: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  employeeId?: number
  isPreorder?: boolean
  scheduledAt?: string | null
}

/** The form as it stands, for the diff below. Resolved values, not the controls. */
export interface EditFields {
  /** The channel's slug, as the chips set it. */
  type: string
  /** Whether that channel seats people - its capability, never its slug (§10A.87). */
  seatsGuests: boolean
  /** `Stôl 5` for a seating channel; ignored otherwise. */
  tableName: string
  customerName: string
  customerPhone: string
  customerEmail: string
  customerAddress: string
  notes: string
  paymentMethod: string
  /** Only sent where the channel seats people, exactly as the create path decides it. */
  waiterId: number | null
  isPreorder: boolean
  /** ISO, or `''`/null where the form holds no time - `scheduledAtOf` answers null. */
  scheduledAt: string | null
}

/**
 * What an edit changes, beyond the lines - §9.383, FB-681.
 *
 * **Only what differs from the order as it was loaded.** A patch that names a field rewrites it, so
 * re-sending an unchanged note would stamp an edit on an order nobody edited - and `PATCH
 * /orders/{id}` distinguishes *absent* from *null* precisely so a caller can say nothing about a
 * field.
 *
 * **The name follows the channel, not the slug.** A channel that seats people carries `Stôl N` as
 * its `customerName` - that is how the board, the map and the dispatch list identify a table
 * (§9.202) - and one that does not carries the person's own name. So switching *Reštaurácia* to
 * *Osobný odber* has to move the name across with it, or the order stays called *Stôl 5* on a
 * channel with no tables.
 */
/**
 * Whether two timestamps name the same minute - §9.383.
 *
 * **A string comparison would have re-sent the time on every save.** The form holds the časenka as a
 * date and a time, so saving recomposes it through `toISOString()` - `…T17:30:00.000Z` - while the
 * order was loaded with whatever the server serialised. Equal instants, different strings, and a
 * patch that names a field rewrites it. Minute precision because that is what the picker offers.
 */
function sameMinute(left: string, right: string): boolean {
  const first = Date.parse(left)
  const second = Date.parse(right)
  if (Number.isNaN(first) || Number.isNaN(second)) return left === right
  return Math.floor(first / 60_000) === Math.floor(second / 60_000)
}

/**
 * What the order is called after this edit - §9.383, and the second half of the owner's report.
 *
 * **A table order is identified by its name.** `Stôl 5` in `customerName` is what *Otvorené stoly*,
 * the table map and the dispatch list all read (§9.202), and the form loads that name into the
 * customer box. So switching the channel to *Osobný odber* and leaving the box alone would send the
 * new channel with the old table name - and the owner said what that looks like: *"objednávka
 * zostáva stále v otvorené stoly, nech ju zmením akokoľvek"*.
 *
 * Three cases, and the middle one is the one that was missing:
 *
 * <ul>
 *   <li>the channel seats people: the name is composed from the table box, as the create path does
 *   <li>it no longer does, and the box still holds the table name nobody typed: **cleared**, because
 *       `Stôl 5` was never a person and a pickup with no name is ordinary
 *   <li>it no longer does, and somebody has typed a name: that name, untouched
 * </ul>
 */
function nameFor(loaded: LoadedOrder, current: EditFields): string {
  if (current.seatsGuests) {
    return current.tableName.trim() === ''
      ? loaded.customerName
      : `${tt('Stôl')} ${current.tableName.trim()}`
  }
  const wasATable = loaded.tableName !== ''
  return wasATable && current.customerName === loaded.customerName ? '' : current.customerName
}

export function editScalars(loaded: LoadedOrder, current: EditFields): EditScalars {
  const scalars: EditScalars = {}
  const name = nameFor(loaded, current)

  if (current.type !== loaded.orderType) scalars.type = current.type
  if (name !== loaded.customerName) scalars.customerName = name
  if (current.customerPhone !== loaded.customerPhone) scalars.customerPhone = current.customerPhone
  if (current.customerEmail !== loaded.customerEmail) scalars.customerEmail = current.customerEmail
  if (current.customerAddress !== loaded.customerAddress)
    scalars.customerAddress = current.customerAddress
  if (current.notes !== loaded.notes) scalars.notes = current.notes
  // §9.40 - a waiter only where a channel seats people. Sending one with a delivery would put the
  // takings against somebody who never touched the order.
  if (current.seatsGuests && current.waiterId !== null) scalars.employeeId = current.waiterId
  if (current.isPreorder !== loaded.isPreorder) scalars.isPreorder = current.isPreorder
  // A časenka's time, and `null` when it stops being one - which is what clears the column.
  if (current.isPreorder) {
    const scheduled = current.scheduledAt ?? ''
    if (scheduled !== '' && !sameMinute(scheduled, loaded.scheduledAt)) {
      scalars.scheduledAt = scheduled
    }
  } else if (loaded.isPreorder) {
    scalars.scheduledAt = null
  }
  // §9.374 - refused only where the money arrived through the method it names.
  if (current.paymentMethod !== loaded.paymentMethod && mayChangePaymentMethod(loaded)) {
    scalars.paymentMethod = current.paymentMethod
    // The board draws *Platba čaká* off `card_online` **or** a pending status, so leaving the old
    // status behind would keep the red badge on an order now paid at the door.
    if (loaded.paymentStatus !== '') scalars.paymentStatus = null
  }
  return scalars
}

export function editPayload(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
  scalars: EditScalars = {},
): Record<string, unknown> | null {
  const added = cart.filter((line) => line.existingId === undefined)
  const live = cart.filter((line) => line.quantity > 0)

  // **Did anything that was already on the order move?** A quantity stepped down, a line removed, a
  // line that is no longer in the cart at all. Only `items` can express any of the three.
  const before = new Map<number, number>()
  for (const line of loaded?.lines ?? []) {
    const id = (line as OrderCartLine & { existingId?: number }).existingId
    if (id !== undefined) before.set(id, line.quantity)
  }
  const now = new Map<number, number>()
  for (const line of live) {
    if (line.existingId !== undefined) now.set(line.existingId, line.quantity)
  }
  const existingChanged =
    before.size > 0 &&
    [...before.entries()].some(([id, quantity]) => (now.get(id) ?? 0) !== quantity)

  const scalarKeys = Object.keys(scalars) as (keyof EditScalars)[]
  const hasScalars = scalarKeys.some((key) => scalars[key] !== undefined)

  if (existingChanged) {
    const items = live.map(itemOf)
    const total =
      live.reduce((sum, line) => sum + parseMoneyToCents(line.unitPrice) * line.quantity, 0) / 100
    return { items, total, ...scalars }
  }
  if (added.length > 0) {
    return { newItems: added.map(itemOf), ...scalars }
  }
  return hasScalars ? { ...scalars } : null
}


/**
 * Whether this order's payment method may still be changed - §9.374, FB-668.
 *
 * **The owner's case is the *failed* one**: *"when objednávka cardpay neprešla, we cant edit it and
 * change it to karta kuriér."* An online payment that never landed has no money against it, so the
 * method is still a question. One that **did** land is not: `online_paid` means the customer's card
 * was charged through CardPay, and relabelling it *Karta kuriér* would put that money in the
 * courier's column and short the handover by exactly the amount.
 *
 * Nothing else is refused here. A cash order changed to card, or the other way, is an ordinary
 * correction; the fiscal guard on the server (`OrderStatusService`'s `lockedPaymentMethod`) is what
 * stops a rewrite after a receipt is printed, and it is not this screen's rule to duplicate.
 */
export function mayChangePaymentMethod(order: LoadedOrder | null): boolean {
  return order === null || order.paymentStatus !== 'online_paid'
}

/** Said next to the selector when the money has already arrived through the method it names. */
export function paymentMethodLockedNote(): string {
  return tt(
    'Online platba už prešla - spôsob platby sa nedá zmeniť, inak by tržba sedela na inom stĺpci.',
  )
}

/**
 * What is about to happen, named before the button is pressed - §9.374.
 *
 * **It used to be able to say only *nothing was added*.** With three branches behind the save, the
 * sentence has to tell a correction from a round from a note: the operator who read *"Zatiaľ nič
 * nepridané"* after decrementing a drink was told the opposite of what they had just done.
 */
export function editSummary(
  cart: readonly (OrderCartLine & { existingId?: number })[],
  loaded?: LoadedOrder | null,
  scalars: EditScalars = {},
): string {
  const added = cart.filter((line) => line.existingId === undefined)
  const live = cart.filter((line) => line.quantity > 0)

  const before = new Map<number, number>()
  for (const line of loaded?.lines ?? []) {
    if (line.existingId !== undefined) before.set(line.existingId, line.quantity)
  }
  const now = new Map<number, number>()
  for (const line of live) {
    if (line.existingId !== undefined) now.set(line.existingId, line.quantity)
  }
  const corrected = [...before.entries()].filter(([id, was]) => (now.get(id) ?? 0) !== was).length

  const parts: string[] = []
  if (added.length > 0) {
    const total = added.reduce(
      (sum, line) => sum + parseMoneyToCents(line.unitPrice) * line.quantity,
      0,
    )
    parts.push(
      `${tt('Pridá sa')} ${added.map((line) => `${line.quantity}× ${line.productName}`).join(', ')} - ${formatCents(total)}`,
    )
  }
  if (corrected > 0) {
    // Said out loud, because it is the one branch that rewrites the order rather than extending it:
    // the kitchen gets no new round for a correction, which is legacy's behaviour and a surprise
    // worth naming on the screen.
    parts.push(`${tt('Opraví sa už zadaných položiek')}: ${corrected} - ${tt('bez nového kola')}`)
  }
  if (scalars.notes !== undefined) parts.push(tt('Zmení sa poznámka'))
  if (scalars.paymentMethod !== undefined) parts.push(tt('Zmení sa spôsob platby'))
  if (parts.length === 0) return tt('Zatiaľ nič nezmenené - objednávka zostane, ako je.')
  return parts.join(' · ')
}

/**
 * Whether this order may be edited at all.
 *
 * `PATCH /orders/{orderId}`'s own summary: *"Edit an order — anything but the three statuses that settle
 * money."* Asking on a settled order and being refused is a worse answer than not offering it.
 */
export const SETTLED = ['completed', 'delivered', 'cancelled']

export function mayEdit(order: LoadedOrder): boolean {
  return !SETTLED.includes(order.status)
}

export function settledNote(order: LoadedOrder): string {
  return `${tt('Objednávka je uzavretá')} (${order.status}) - ${tt('upraviť sa už nedá; peniaze sú vyrovnané. Doobjednávka je nová objednávka.')}`
}
